/**
 * Sound on by default for the practice films (they carry the app's real coach voice), within what browsers allow.
 *
 * A browser only lets a page make sound once the person has done something on it (a tap, a click, a key). So:
 *   - after the person has already tapped or clicked anywhere on the site, every practice film that scrolls into view speaks by itself;
 *   - before that, the film plays muted and waits: the first tap, click or key press anywhere switches the film in view to sound,
 *     restarting it so the voice starts with its first line (the gesture is what lets the browser allow it);
 *   - if the person ever taps "Mute", nothing switches sound on by itself again in this visit;
 *   - only one film speaks at a time.
 */
let speaker = null; // the mute() of the film that is speaking now
let userMuted = false;
const waiting = new Map(); // films in view that would speak if the browser allowed it: id -> speak(by)
let armed = false;

/** True once the person has tapped, clicked or pressed a key on this page (Chrome, Safari, Edge); unknown elsewhere. */
export function userHasActed() {
  try { return !!navigator.userActivation?.hasBeenActive; } catch { return false; }
}

export const autoSoundAllowed = () => !userMuted;
export const markUserMuted = () => { userMuted = true; };

/** The film that is about to speak silences the one that spoke before. */
export function claimSpeaker(mute) {
  if (speaker && speaker !== mute) speaker();
  speaker = mute;
}
export function releaseSpeaker(mute) { if (speaker === mute) speaker = null; }

// pointerdown/mousedown count as activation for a mouse, so the voice starts on the press, not the release; touchend/pointerup are the touch ones
const EVENTS = ["pointerdown", "mousedown", "pointerup", "touchend", "click", "keydown"];

function onGesture(e) {
  if (e && e.type === "pointerdown" && e.pointerType !== "mouse") return; // a finger's press is not yet an activation: wait for touchend / pointerup
  EVENTS.forEach((e) => window.removeEventListener(e, onGesture, true));
  armed = false;
  if (userMuted) return;
  const first = waiting.values().next().value;
  if (first) first("gesture"); // inside the gesture, which is what lets play() with sound through
}

/** This film is in view and wants to speak: do it at the next gesture (or now, if the person has already acted). */
export function waitForGesture(id, speak) {
  waiting.set(id, speak);
  if (armed || typeof window === "undefined") return;
  armed = true;
  EVENTS.forEach((e) => window.addEventListener(e, onGesture, true));
}
export function stopWaiting(id) { waiting.delete(id); }
